import { navigate } from 'gatsby-link';
import { Statsig } from 'statsig-react';
import { gTagAvailability, gTagEvent } from './googleAnalyticsHelper';
import { getLocalStorage } from './localStorageHandler';

const getCookies = () => {
  return document.cookie.split('; ').reduce(
    (p, c) => {
      const [key, val] = c.split(/=(.*)/s);
      p[key] = val;

      return p;
    },
    {} as { [key: string]: string }
  );
};

const filterQueryParams = (): string => {
  const searchString = location?.search;
  if (!searchString) {
    return '';
  }

  const applicableQueries = searchString
    .replace('?', '')
    .split('&')
    .filter((param) => {
      // We're only interested in utm queries deriving from adds
      const candidate = param.split('=')[0];
      return (
        candidate.includes('utm') ||
        candidate.includes('msclkid') ||
        candidate.includes('li_fat_id') ||
        candidate.includes('fbclid') ||
        candidate.includes('gclid')
      );
    })
    .join('&');

  if (applicableQueries.length) {
    return `${applicableQueries}`;
  } else {
    return '';
  }
};

export const propagateUtmParams = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  // Explicitely using "window" to avoid "not found" declaration
  const currentQueryPass = window.sessionStorage.getItem('query_pass');
  if (currentQueryPass) {
    const queryPass = filterQueryParams();
    // Is it the same values? Else repalce
    if (queryPass && queryPass !== currentQueryPass) {
      window.sessionStorage.setItem('query_pass', queryPass);
      return queryPass.replace('?', '');
    }
    return currentQueryPass.replace('?', '');
  } else {
    const queryPass = filterQueryParams();
    if (queryPass) {
      window.sessionStorage.setItem('query_pass', queryPass);
      return queryPass;
    } else {
      return '';
    }
  }
};

const propagateGaParams = (valueOnly: boolean): string => {
  let { _gid, _ga } = getCookies() as { _gid: string; _ga: string };
  if (!_gid || !_ga) {
    // _gid has not been set
    (window as any).TrackJS && (window as any).TrackJS.addMetadata('analytics_status', 'missing');
    return '';
  }
  (window as any).TrackJS && (window as any).TrackJS.addMetadata('analytics_status', 'existing');

  // >> Make sure _ga is cleared of version tagging
  _gid = _gid.split('.').slice(2).join('.');
  _ga = _ga.split('.').slice(2).join('.');
  const timeSequenceAsStrings = (a: string) => {
    let b = 1,
      c;
    if (a)
      for (b = 0, c = a.length - 1; 0 <= c; c--) {
        let d = a.charCodeAt(c);
        b = ((b << 6) & 268435455) + d + (d << 14);
        d = b & 266338304;
        b = 0 != d ? b ^ (d >> 21) : b;
      }
    return b;
  };

  const parseTimeStamp = (a: string, b: number) => {
    const c = new Date(),
      NAVIGATOR = window.navigator,
      e = c.getHours() + Math.floor((c.getMinutes() + b) / 60);
    // (c.getFullYear() - 1900) replaces deprecated c.getYear() API
    return timeSequenceAsStrings(
      [
        a,
        NAVIGATOR.userAgent,
        NAVIGATOR.language || '',
        c.getTimezoneOffset(),
        c.getFullYear() - 1900,
        c.getDate() + Math.floor(e / 24),
        (24 + e) % 24,
        (60 + c.getMinutes() + b) % 60,
      ].join('.')
    );
  };
  if (valueOnly) {
    return `2.${encodeURIComponent(parseTimeStamp(_gid + _ga, 0) + '.' + _gid + '-' + _ga)}`;
  }
  return `_ga=2.${encodeURIComponent(parseTimeStamp(_gid + _ga, 0) + '.' + _gid + '-' + _ga)}`;
};

const propagateStatsigUserId = (href: string) => {
  const userid = typeof window === 'undefined' ? null : getLocalStorage('statsigUserId');
  if (!href.includes('.mn.co') && !href.includes('/path_to_pro_offer')) {
    return '';
  }
  if (userid) {
    return `experimentVisitorId=${userid}`;
  }
  return '';
};

const propagateSegmentAnonymousId = () => {
  if (typeof window !== 'undefined') {
    const { ajs_anonymous_id: ajs_anonymous_id_cookie } = getCookies();
    // localstorage comes as a quoted string `"6ad26866-..."`
    const ajs_anonymous_id_storage = (localStorage.getItem('ajs_anonymous_id') || '').split('"').join('');
    const ajs_anonymous_id_script = (window as any).analytics?.user
      ? (window as any).analytics?.user()?.anonymousId()
      : '';
    // Fallback sequence ORs
    const ai = ajs_anonymous_id_script || ajs_anonymous_id_storage || ajs_anonymous_id_cookie || '';
    if (ai) {
      return `ai=${ai}`;
    }
    return '';
  }
};

const propagateRewardful = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const { rewardfulViaId } = getCookies();

  if (rewardfulViaId) {
    return `via=${rewardfulViaId}`;
  } else {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('via')) {
      return `via=${queryParams.get('via')}`;
    }
  }

  return '';
};

/**
 * Get stored rewardful ID from query parameter or cookie
 * @returns string
 */
export const getRewardfulAffiliateId = (): string => {
  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.has('via')) {
    return queryParams.get('via');
  }

  const { rewardfulViaId } = getCookies();

  return rewardfulViaId ?? '';
};

const propagateAllAvailable = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const { queryStringParams } = getCookies();
  const resultString = [];

  if (queryStringParams) {
    const queryStringParamsParsed = JSON.parse(queryStringParams) as { [key: string]: string };

    for (const key in queryStringParamsParsed) {
      resultString.push(`${key}=${queryStringParamsParsed[key]}`);
    }
  } else {
    const queryParams = new URLSearchParams(window.location.search);

    queryParams.forEach((value, key) => {
      resultString.push(`${key}=${value}`);
    });
  }

  if (resultString.length) {
    return resultString.join('&');
  }

  return '';
};

export const propagateQuerytoExternalLinks = (href: string): string => {
  if (urlIsExternal(href) && typeof window !== 'undefined') {
    href = href.replace('new.mn.co', 'cohost.mn.co');

    if (process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE) {
      // This intends to replace original product page endpoint with another for testing purposes
      href = href.replace('cohost.mn.co', process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE);
    }

    const queryPropagateRaw = [
      propagateUtmParams(),
      propagateGaParams(false),
      propagateStatsigUserId(href),
      propagateSegmentAnonymousId(),
      propagateRewardful(href),
      propagateAllAvailable(href),
    ]
      .filter((s) => !!s)
      .join('&');

    const queryPropagate = [...new Set(queryPropagateRaw.split('&'))].join('&');

    if (!queryPropagate) {
      return href;
    } else if (`${href}${queryPropagate}`.includes('?')) {
      // >> possibly href includes a query already 'mn.co?type=me
      return `${href}&${queryPropagate}`;
    }
    return `${href}?${queryPropagate}`;
    // return href;
  } else {
    return href;
  }
};

export const urlIsExternal = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const temporaryAnchor = document.createElement('a');
  temporaryAnchor.href = url;

  return temporaryAnchor.hostname !== window.location.hostname;
};

// On click Link Track Propagation
// ===========================================

const reApplyUtmQueryToUrl = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  propagateUtmParams()
    .split('&')
    .filter((s) => !!s)
    .forEach((field) => {
      const [key, value] = field.split('=');
      if (searchParams.has(key)) {
        searchParams.delete(key);
      }
      searchParams.append(key, value);
    });
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

const reApplyGaQueryToUrl = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  const ga = propagateGaParams(true);
  if (searchParams.has('_ga') && !!ga) {
    searchParams.delete('_ga');
    searchParams.append('_ga', ga);
  } else if (ga) {
    searchParams.append('_ga', ga);
  }
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

const reApplyStatsigUserId = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  const statsigParam = propagateStatsigUserId(url);
  if (!statsigParam) {
    // Only applied to .mn.co url, if empty, no change
    return urlBase;
  }
  const [key, value] = statsigParam.split('=');
  if (searchParams.has(key)) {
    searchParams.delete(key);
  }
  searchParams.append(key, value);
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

export const reApplyTrackingToUrl = (url: string): string => {
  url = reApplyUtmQueryToUrl(url);
  url = reApplyGaQueryToUrl(url);
  url = reApplyStatsigUserId(url);
  return url;
};

export const applyGanalyticsTracking =
  <T extends HTMLElement = HTMLAnchorElement>(queryPropagatedUrl: string, hrefOriginal: string, useSameTab = false) =>
    (event: React.MouseEvent<T, MouseEvent>): void => {
      event.preventDefault();
      if (urlIsExternal(queryPropagatedUrl)) {
        // External links to MN.CO,
        // >> Ga needs updating as it depends on timestamp
        const trackedUrl = reApplyTrackingToUrl(queryPropagatedUrl);
        if (!trackedUrl.includes('_ga')) {
          (window as any).TrackJS && (window as any).TrackJS.console.error('_ga missing!', { url: trackedUrl });
          (window as any).TrackJS && (window as any).TrackJS.track(`_ga missing! ${trackedUrl}`);
        }
        // Statsig event tracking
        // Track every call to action when leaving mightynetworks.com to cohost.mn.co
        if (
          queryPropagatedUrl.includes(`new.mn.co`) ||
          queryPropagatedUrl.includes(`cohost.mn.co`) ||
          queryPropagatedUrl.includes(process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE)
        ) {
          Statsig.logEvent('cta_clicked', 1);
        }
        // ------------------------
        // External links to other domains, some cases are opened in same tab
        if (useSameTab) {
          window.location.href = trackedUrl;
        } else {
          window.open(trackedUrl, '_blank').focus();
        }
      } else {
        if (queryPropagatedUrl.includes(`/request-a-call-form`)) {
          Statsig.logEvent('request_a_call_clicked', 1);
        }

        // internal are defered to Gatsby (internals are faster)
        navigate(hrefOriginal);
      }
    };

export const trackButtonActivity = async (
  elem: HTMLAnchorElement | HTMLButtonElement,
  text: string = null,
  section: string = null
): Promise<void> => {
  // Async helps not to wait for Element parsing before triggering button | anchor event
  const analytics = (window as any).analytics;
  const unTrackable = !elem && !(section && text); // If no element, at least section & text
  if (!analytics?.user || unTrackable || !gTagAvailability()) {
    return;
  }

  // Crawl to look for section and text
  const recursiveElementCheckForHeader = (parentElement: HTMLElement): string => {
    const headers = parentElement.getElementsByTagName('h1');
    const namedSection = parentElement.querySelectorAll(`[data-section-name]`);

    const sectionLabels = [];
    if (parentElement.tagName.toUpperCase() === 'MAIN') {
      // This is maximum search crawl, asume orphan element with no section
      return 'root';
    }
    if (headers.length && !parentElement.getAttribute(`data-section-name`)) {
      // Avoids tracking specific headers
      if (!headers[0].getAttribute('data-section-notrack')) {
        sectionLabels.push(headers[0].textContent);
      }
    }
    if (namedSection.length) {
      sectionLabels.push(namedSection[0].getAttribute(`data-section-name`));
    } else if (parentElement.getAttribute(`data-section-name`)) {
      sectionLabels.push(parentElement.getAttribute(`data-section-name`));
    }
    if (sectionLabels.length) {
      return sectionLabels.join('|');
    }
    return recursiveElementCheckForHeader(parentElement.parentElement);
  };
  const textIs = text || elem.getAttribute(`aria-label`) || elem.textContent || '';
  const sectionIs = section ? section : recursiveElementCheckForHeader(elem.parentElement);
  const trackLabel = `${sectionIs}|${textIs}`;

  gTagEvent('Automatic_Link_Tracking', {
    category: `Link Click`,
    label: trackLabel,
  });
};
